/**
 * 配達日時指定(宅急便のみ)。
 *
 * 非Plusのためチェックアウト画面には項目を追加できない。カートで選ばせて
 * cart attributes へ保存し、注文 → 倉庫CSV(32列目 配送日 / 33列目 配送時間帯)へ渡す。
 *
 * 選択肢の日付はアプリのApp Proxy(/apps/plus2do-options/delivery-schedule)から取得する。
 * 祝日・臨時休業日・14時カットオフの判定はアプリの発送日カレンダーが単一ソースのため、
 * テーマ側では計算しない(管理画面の設定と乖離させない)。
 *
 * 保存先: attributes[配達希望日] = "2026/08/20" / attributes[配達希望時間帯] = "午前中" 等
 * 値の書式は倉庫確定(2026-08-17)。
 */
class CartDeliverySchedule extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.endpoint = this.dataset.endpoint || '/apps/plus2do-options/delivery-schedule';
    this.dateSelect = this.querySelector('[data-delivery-date]');
    this.slotSelect = this.querySelector('[data-delivery-slot]');
    this.panel = this.querySelector('[data-delivery-panel]');
    this.status = this.querySelector('[data-delivery-status]');
    this.shipNote = this.querySelector('[data-delivery-ship-note]');
    this.savedDate = this.dataset.savedDate || '';
    this.savedSlot = this.dataset.savedSlot || '';

    if (!this.dateSelect || !this.slotSelect) return;

    this.dateSelect.addEventListener('change', () => this.persist());
    this.slotSelect.addEventListener('change', () => this.persist());

    this.load();
  }

  async load() {
    try {
      const response = await fetch(this.endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`status ${response.status}`);
      const data = await response.json();
      if (!data || data.ok !== true || !Array.isArray(data.dates) || data.dates.length === 0) {
        // 機能停止中・未設定などはUIごと隠す(誤った日付を選ばせない)。
        this.hide();
        return;
      }
      this.render(data);
    } catch (error) {
      this.hide();
    }
  }

  hide() {
    this.hidden = true;
    // hidden 属性だけでは select はカートフォームと一緒に送信されてしまう。
    // 選択肢を出せていない状態で空値を送ると、保存済みの配達希望を消してしまうため無効化する。
    if (this.dateSelect) this.dateSelect.disabled = true;
    if (this.slotSelect) this.slotSelect.disabled = true;
  }

  render(data) {
    this.fillOptions(this.dateSelect, data.dates, this.savedDate);
    this.fillOptions(
      this.slotSelect,
      (data.timeSlots || []).map((slot) => ({ value: slot, label: slot })),
      this.savedSlot
    );

    if (this.shipNote && data.shipDateLabel) {
      this.shipNote.textContent = data.shipsToday
        ? `本日(${data.shipDateLabel})発送予定です。`
        : `${data.shipDateLabel}に発送予定です(${data.cutoffTime || '14:00'}以降のご注文は翌営業日発送)。`;
    }

    if (this.panel) this.panel.hidden = false;
  }

  fillOptions(select, options, saved) {
    // 先頭の「指定なし」は Liquid 側で出力済みなので消さない。
    while (select.options.length > 1) select.remove(1);

    options.forEach((option) => {
      const element = document.createElement('option');
      element.value = option.value;
      element.textContent = option.label;
      select.appendChild(element);
    });

    // 保存済みの値が選択肢に残っていれば復元する(日付が過ぎた場合は「指定なし」に戻る)。
    if (saved && Array.from(select.options).some((option) => option.value === saved)) {
      select.value = saved;
    }
  }

  setStatus(message, isError) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('cart-delivery-schedule__status--error', Boolean(isError));
  }

  async persist() {
    const date = this.dateSelect.value;
    const slot = this.slotSelect.value;

    // 日付だけ・時間帯だけの指定も倉庫は受け付けるため、片方だけでも保存する。
    this.setStatus('保存中…', false);
    try {
      const response = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 配達希望日: date, 配達希望時間帯: slot } }),
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
      this.setStatus(date || slot ? '配達希望を保存しました。' : '配達希望を「指定なし」にしました。', false);
    } catch (error) {
      this.setStatus('保存できませんでした。通信環境をご確認のうえ、もう一度お試しください。', true);
    }
  }
}

if (!customElements.get('cart-delivery-schedule')) {
  customElements.define('cart-delivery-schedule', CartDeliverySchedule);
}
